import { useRef } from "react";
import { fmt, signed, pct } from "../lib/format";
import { GradeBadge } from "./GradeBadge";

const TrendPill = ({ bias }) => {
  const map = {
    BULLISH: "text-green-500 bg-green-500/10 border-green-500/20",
    BEARISH: "text-red-500 bg-red-500/10 border-red-500/20",
    NEUTRAL: "text-zinc-400 bg-zinc-500/10 border-zinc-600/30",
  };
  return (
    <span className={`px-1 py-[1px] text-[9px] font-semibold rounded-sm border ${map[bias] || map.NEUTRAL}`}>
      {bias?.slice(0, 4)}
    </span>
  );
};

const CRYPTO = new Set(["MBT", "MET", "XRP"]);

const reasonStyle = (r = "") => {
  if (/^ENTER|^Entered/i.test(r)) return { dot: "bg-green-500", text: "text-green-400" };
  if (/await|confirm|MICRO mode/i.test(r)) return { dot: "bg-amber-400", text: "text-amber-300" };
  if (/already holding|max position|lockout|cooldown|blocked|halted|stopped|session|syncing|cap reached/i.test(r))
    return { dot: "bg-orange-500", text: "text-orange-300" };
  return { dot: "bg-zinc-600", text: "text-zinc-500" };
};

const SourcePill = ({ source, symbol }) => {
  let text, cls;
  if (source === "BROKER") { text = "CME LIVE"; cls = "text-green-400 bg-green-500/10 border-green-500/30"; }
  else if (source === "MARKET" && CRYPTO.has(symbol)) { text = "KRAKEN"; cls = "text-cyan-300 bg-cyan-500/10 border-cyan-500/25"; }
  else if (source === "MARKET") { text = "YAHOO·DLY"; cls = "text-amber-400 bg-amber-500/10 border-amber-500/25"; }
  else { text = "SIM"; cls = "text-zinc-400 bg-zinc-500/10 border-zinc-600/30"; }
  return (
    <span data-testid={`source-pill-${symbol}`} title={`Data source: ${source || "SIM"}`}
      className={`px-1 py-[1px] text-[8px] font-bold rounded-sm border tracking-wide ${cls}`}>
      {text}
    </span>
  );
};

export const MarketWatch = ({ market, selected, onSelect, threshold }) => {
  const prev = useRef({});
  return (
    <div className="flex-1 min-h-0 overflow-auto thin-scroll">
      {(market || []).map((m) => {
        const p = prev.current[m.symbol];
        const flash = p == null ? "" : m.price > p ? "flash-up" : m.price < p ? "flash-down" : "";
        prev.current[m.symbol] = m.price;
        const active = selected === m.symbol;
        const scoreHot = m.score >= threshold;
        return (
          <button
            key={m.symbol}
            data-testid={`market-row-${m.symbol}`}
            onClick={() => onSelect(m.symbol)}
            className={`w-full text-left px-3 py-2 border-b border-zinc-800/80 hover:bg-zinc-800/40 transition-colors duration-150 ${flash} ${active ? "bg-blue-500/10 border-l-2 border-l-blue-500" : "border-l-2 border-l-transparent"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[13px] font-bold text-zinc-100">{m.symbol}</span>
                <TrendPill bias={m.trend?.bias} />
                <SourcePill source={m.source} symbol={m.symbol} />
              </div>
              <span className="font-mono text-[13px] font-semibold text-zinc-100">
                {fmt(m.price, m.price > 1000 ? 2 : 2)}
              </span>
            </div>
            <div className="flex items-center justify-between mt-0.5">
              <span className="text-[10px] text-zinc-500 truncate max-w-[90px]">{m.name}</span>
              <div className="flex items-center gap-2">
                <span className={`font-mono text-[10.5px] ${m.change >= 0 ? "text-green-500" : "text-red-500"}`}>
                  {signed(m.change, 2)} ({pct(m.change_pct)})
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1">
                <span className="text-[9px] uppercase tracking-wide text-zinc-500">Conf</span>
                <div className="flex gap-[2px]">
                  {[...Array(11)].map((_, i) => (
                    <span
                      key={i}
                      className={`h-2 w-[3px] rounded-[1px] ${i < (m.score || 0) ? (scoreHot ? "bg-blue-400" : "bg-zinc-500") : "bg-zinc-800"}`}
                    />
                  ))}
                </div>
                <span className={`font-mono text-[10px] ${scoreHot ? "text-blue-400 font-bold" : "text-zinc-400"}`}>
                  {m.score || 0}
                </span>
              </div>
              {m.direction && (
                <div className="flex items-center gap-1">
                  <GradeBadge grade={m.grade} />
                  <span className={`px-1 text-[9px] font-bold rounded-sm ${m.direction === "LONG" ? "text-green-500 bg-green-500/10" : "text-red-500 bg-red-500/10"}`}>
                    {m.direction}
                  </span>
                </div>
              )}
            </div>
            {m.skip_reason && (() => { const rs = reasonStyle(m.skip_reason); return (
              <div data-testid={`skip-reason-${m.symbol}`} className="flex items-center gap-1 mt-1" title={m.skip_reason}>
                <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${rs.dot}`} />
                <span className={`text-[9px] leading-tight truncate ${rs.text}`}>{m.skip_reason}</span>
              </div>
            ); })()}
          </button>
        );
      })}
    </div>
  );
};
